import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, inject, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { FormBuilder, FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import {select, Store} from '@ngrx/store';
import {ForgetPassword, getError, getForgetPassword, getLoggedInUser, IAuthenticationState, ResetAuthState} from '@app/stores';
import {ToastrService} from 'ngx-toastr';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {mergeAuthTranslations} from '../auth-i18n';
import { AuthMarketingPanelComponent } from '../auth-marketing-panel/auth-marketing-panel.component';

@Component({
  templateUrl: './forgot-password.component.html',
  styleUrls: ['./forgot-password.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, RouterLink, AuthMarketingPanelComponent, TranslateModule]
})
export class ForgotPasswordComponent implements OnInit, OnDestroy {
  private formBuilder = inject(FormBuilder);
  private router = inject(Router);
  private toastrService = inject(ToastrService);
  private authenticationStore = inject<Store<IAuthenticationState>>(Store);
  private translate = inject(TranslateService);

  passwordForm: FormGroup<{email: FormControl<string | null>}>;
  submitted = false;
  readonly passMessage = signal<any>({
    success: false,
    msg: ''
  });
  // True from the moment the address is sent until the store answers - with the
  // request accepted or with an error. The button is disabled meanwhile, so one
  // click is one request.
  readonly sendPending = signal(false);
  unsubscriber = new Subject();

  constructor() {
    this.authenticationStore.dispatch(ResetAuthState({params: {error: '', forgetPassword: null}}));
    this.subscribeStores();
  }

  subscribeStores() {
    // redirect to home if already logged in
    this.authenticationStore.pipe(select(getLoggedInUser))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(user => {
        if (user) {
          this.router.navigate(['/']);
        }
      });

    this.authenticationStore.pipe(select(getForgetPassword))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(forgetPassword => {
        if (forgetPassword) {
          this.sendPending.set(false);
          this.passMessage.set(forgetPassword);
          if (forgetPassword.success) {
            this.toastrService.success('Password reset link has been sent to your email.');
            this.router.navigate(['/login']);
          }
        }
      });

    this.authenticationStore.pipe(select(getError))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(error => {
        if (error) {
          this.sendPending.set(false);
          this.toastrService.error(error);
        }
      });
  }

  ngOnInit() {
    mergeAuthTranslations(this.translate, this.unsubscriber);

    this.passwordForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]]
    });
  }

  get f() {
    return this.passwordForm.controls;
  }

  /**
   * Whatever the server said about the request, if it said anything.
   *
   * The reply is deliberately the same for every address - it never says whether
   * an account exists - so it is safe to show verbatim, and safer than wording of
   * our own that might not be.
   */
  get responseMessage(): string {
    return this.passMessage()?.message || this.passMessage()?.msg || '';
  }

  /**
   * Returns success message object that will use for Showing the response.
   *
   * @param  url  an absolute URL giving the reset Forgot password page of the Application.
   * @param  name the location of the request, relative to the url.
   * @return      success object with temporary login URL.
   */

  onSubmit() {
    this.submitted = true;

    // stop here if form is invalid, or the previous request has not answered yet
    if (this.passwordForm.invalid || this.sendPending()) {
      return;
    }
    this.sendPending.set(true);
    this.authenticationStore.dispatch(ForgetPassword({email: this.passwordForm.value.email}));
  }

  ngOnDestroy() {
    this.unsubscriber.next(undefined);
    this.unsubscriber.complete();
  }
}
